/**
 * Myselfie global header date picker.
 *
 * Self-contained, dependency-free (ES2019, no modules, loaded with `defer`):
 *   - date helpers            DD/MM/YYYY <-> Date <-> YYYY-MM-DD
 *   - sessionStorage helpers  key "wdn_bookingdate", value "DD/MM/YYYY"
 *   - availability client     GET {availabilityUrl} (cached, fails open)
 *   - <myselfie-datepicker>   month calendar custom element
 *   - <myselfie-header-picker> input + icon button + status line
 *
 * Configuration comes from snippets/myselfie-config.liquid via window.myselfie.
 * Nothing here throws when window.myselfie is missing; sensible defaults are used.
 *
 * The product pages keep their own assets/api-dates.js (jQuery UI). When such a
 * picker is present on the page (#productpage-booking-start) the header picker
 * fills it instead of navigating away.
 */
(function () {
  'use strict';

  var cfg = (window.myselfie = window.myselfie || {});
  cfg.i18n = cfg.i18n || {};
  cfg.maxYears = Number(cfg.maxYears) || 3;

  var STORAGE_KEY = 'wdn_bookingdate';
  var PRODUCT_INPUT_SELECTOR = '#productpage-booking-start';

  /* ------------------------------------------------------------------ */
  /* Date helpers                                                        */
  /* ------------------------------------------------------------------ */
  function pad(n) {
    return String(n).length < 2 ? '0' + n : String(n);
  }
  /** Date -> "YYYY-MM-DD" */
  function toIso(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }
  /** Date -> "DD/MM/YYYY" */
  function toDisplay(d) {
    return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear();
  }
  /** "DD/MM/YYYY" (or already ISO) -> "YYYY-MM-DD", '' when unparseable */
  function displayToIso(s) {
    if (!s) return '';
    var m = String(s).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) return m[3] + '-' + pad(m[2]) + '-' + pad(m[1]);
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(s))) return String(s);
    return '';
  }
  /** "YYYY-MM-DD" -> Date (local midnight) */
  function isoToDate(iso) {
    var parts = String(iso).split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  function today() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /* ------------------------------------------------------------------ */
  /* Storage (sessionStorage "wdn_bookingdate" = "DD/MM/YYYY")           */
  /* ------------------------------------------------------------------ */
  var storage = {
    read: function () {
      try {
        return sessionStorage.getItem(STORAGE_KEY) || '';
      } catch (e) {
        return '';
      }
    },
    save: function (display) {
      try {
        if (display) sessionStorage.setItem(STORAGE_KEY, display);
        else sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* storage blocked (private mode / cookie banner) */
      }
    },
    clear: function () {
      this.save('');
    },
  };

  /* ------------------------------------------------------------------ */
  /* Availability client                                                 */
  /* ------------------------------------------------------------------ */
  /**
   * GET https://api.myselfie.com/public/v1/{shop}/bookeddatesavailability?store={shop}
   *   {
   *     "aggregate": { "sold_out": [{ "start": "2026-06-01", "end": "2026-06-03" }], "low": [...], "popular": [...] },
   *     "products":  { "Myselfie": { "sold_out": [...], "low": [...] }, ... }
   *   }
   * Ranges are INCLUSIVE. On any error the client fails open: every date is
   * treated as available and the picker keeps working.
   */
  var availability = {
    _promise: null,
    data: null,
    failed: false,
    url: function () {
      if (cfg.availabilityUrl) return cfg.availabilityUrl;
      var shop = (window.Shopify && Shopify.shop ? String(Shopify.shop) : '').split('.')[0];
      var base = String(cfg.apiBase || 'https://api.myselfie.com/public/v1').replace(/\/$/, '');
      return base + '/' + shop + '/bookeddatesavailability?store=' + shop;
    },
    /** Expand [{start,end}] (inclusive) into a Set of ISO dates. */
    expand: function (ranges) {
      var set = {};
      if (!ranges || !ranges.length) return set;
      for (var r = 0; r < ranges.length; r++) {
        var range = ranges[r];
        if (!range) continue;
        var startIso = String(range.start || range.from || '');
        var endIso = String(range.end || range.to || startIso);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso)) continue;
        var cursor = isoToDate(startIso);
        var last = /^\d{4}-\d{2}-\d{2}$/.test(endIso) ? isoToDate(endIso) : isoToDate(startIso);
        // guard against a broken range blocking the thread
        for (var i = 0; cursor <= last && i < 2000; i++) {
          set[toIso(cursor)] = true;
          cursor.setDate(cursor.getDate() + 1);
        }
      }
      return set;
    },
    _index: function (json) {
      var self = this;
      var build = function (node) {
        return {
          soldOut: self.expand(node && node.sold_out),
          low: self.expand(node && node.low),
          // high demand on the event date (optional; older API responses have no "popular")
          popular: self.expand(node && node.popular),
          // lead block (shipping lead time), computed by the backend: ranges like sold_out. Missing = no lead days.
          lead: self.expand(node && node.lead),
        };
      };
      var products = {};
      var raw = (json && json.products) || {};
      Object.keys(raw).forEach(function (key) {
        products[String(key).toLowerCase()] = build(raw[key]);
      });
      return { aggregate: build(json && json.aggregate), products: products };
    },

    /** @returns {Promise<Object>} indexed availability, memoised, never rejects */
    load: function () {
      var self = this;
      if (!this._promise) {
        this._promise = fetch(this.url(), { headers: { Accept: 'application/json' } })
          .then(function (r) {
            if (!r.ok) throw new Error('bookeddatesavailability ' + r.status);
            return r.json();
          })
          .then(function (json) {
            return self._index(json);
          })
          .catch(function (err) {
            // Fail open: no availability data means "everything is bookable".
            if (window.console && console.warn) {
              console.warn('[myselfie] availability unavailable, picker fails open', err);
            }
            self.failed = true;
            return { aggregate: { soldOut: {}, low: {}, popular: {}, lead: {} }, products: {} };
          })
          .then(function (data) {
            self.data = data;
            return data;
          });
      }
      return this._promise;
    },
    /** Normalise "Myselfie,Printer" | ["Myselfie"] | undefined into a list of keys. */
    keys: function (components) {
      var list = components;
      if (typeof list === 'string') list = list.split(',');
      if (!list || !list.length) list = [];
      list = list
        .map(function (s) {
          return String(s).trim().toLowerCase();
        })
        .filter(Boolean);
      return list.length ? list : ['aggregate'];
    },
    /** Merged { soldOut, low, popular } maps for the given components. */
    setsFor: function (components) {
      var data = this.data;
      if (!data) return { soldOut: {}, low: {}, popular: {}, lead: {} };
      var nodes = [];
      this.keys(components).forEach(function (key) {
        if (key === 'aggregate') nodes.push(data.aggregate);
        else if (data.products[key]) nodes.push(data.products[key]);
      });
      if (!nodes.length) nodes.push(data.aggregate);
      if (nodes.length === 1) return nodes[0];
      var merged = { soldOut: {}, low: {}, popular: {}, lead: {} };
      nodes.forEach(function (node) {
        Object.keys(node.lead || {}).forEach(function (iso) {
          merged.lead[iso] = true;
        });
        Object.keys(node.soldOut).forEach(function (iso) {
          merged.soldOut[iso] = true;
        });
        Object.keys(node.low).forEach(function (iso) {
          merged.low[iso] = true;
        });
        Object.keys(node.popular || {}).forEach(function (iso) {
          merged.popular[iso] = true;
        });
      });
      return merged;
    },
    /** @returns {boolean} lead-block day (backend field "lead") for the components; wins over sold_out/low/popular. */
    isLead: function (iso, components) {
      var sets = this.setsFor(components);
      return !!(sets.lead && sets.lead[iso]);
    },
    /** @returns {'sold_out'|'low'|'popular'|'available'} lead/sold_out > low > popular */
    statusFor: function (iso, components) {
      var sets = this.setsFor(components);
      // a lead day cannot be booked either: callers that only block on 'sold_out' keep working;
      // display code asks isLead() first so it never shows it as "fully booked".
      if (sets.lead && sets.lead[iso]) return 'sold_out';
      if (sets.soldOut[iso]) return 'sold_out';
      if (sets.low[iso]) return 'low';
      if (sets.popular && sets.popular[iso]) return 'popular';
      return 'available';
    },
  };
  cfg.availability = availability;

  /* ------------------------------------------------------------------ */
  /* <myselfie-datepicker>                                               */
  /* ------------------------------------------------------------------ */
  function MyselfieDatepicker() {
    return Reflect.construct(HTMLElement, [], MyselfieDatepicker);
  }
  MyselfieDatepicker.prototype = Object.create(HTMLElement.prototype);
  MyselfieDatepicker.prototype.constructor = MyselfieDatepicker;
  Object.setPrototypeOf(MyselfieDatepicker, HTMLElement);

  var dp = MyselfieDatepicker.prototype;

  dp.connectedCallback = function () {
    if (this._bound) return;
    this.input = this.querySelector('input');
    if (!this.input) return;
    this._bound = true;
    this.calendar = null;
    this.booked = {};
    this.low = {};
    this.popular = {};
    this.lead = {};
    this.ready = false;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onInsideClick = this.onInsideClick.bind(this);
    // Capture phase on the element itself: this always runs before the
    // document-level outside-click handler, even when the click target is
    // removed from the DOM by render() in between (nav buttons, selects).
    this.addEventListener('mousedown', this.onInsideClick, true);
    this.addEventListener('click', this.onInsideClick, true);

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('inputmode', 'none');
    var self = this;
    this.input.addEventListener('keydown', function (e) {
      // readonly-like behaviour without the readonly attribute
      if (e.key === 'Tab' || e.key === 'Escape') return;
      e.preventDefault();
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') self.open();
    });
    this.input.addEventListener('click', function () {
      self.open();
    });
    this.input.addEventListener('focus', function () {
      self.open();
    });
    this.input.addEventListener('paste', function (e) {
      e.preventDefault();
    });

    var min = today();
    min.setDate(min.getDate() + 1);
    this.minDate = min;
    var max = today();
    max.setFullYear(max.getFullYear() + cfg.maxYears);
    this.maxDate = max;

    // Prefill from sessionStorage (a date chosen on another page).
    var stored = storage.read();
    if (stored && !this.input.value) {
      var storedIso = displayToIso(stored);
      if (storedIso && isoToDate(storedIso) >= this.minDate) this.input.value = stored;
    }

    this.setAttribute('data-loading', '');
    availability.load().then(function () {
      var sets = availability.setsFor(self.components);
      self.booked = sets.soldOut;
      self.low = sets.low;
      self.popular = sets.popular || {};
      self.lead = sets.lead || {};
      self.ready = true;
      self.removeAttribute('data-loading');
      if (self.calendar) self.render();
    });
  };

  dp.disconnectedCallback = function () {
    this.close();
  };

  Object.defineProperty(dp, 'valueIso', {
    get: function () {
      return this.input ? displayToIso(this.input.value) : '';
    },
  });

  Object.defineProperty(dp, 'components', {
    get: function () {
      return this.dataset.availabilityProducts || 'aggregate';
    },
  });

  /** A lead-block day for this picker's products (the lead set is emptied for products the API does not list). */
  dp.isLeadDay = function (iso) {
    return !!this.lead[iso];
  };

  dp.isSelectable = function (date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;
    if (date < this.minDate || date > this.maxDate) return false;
    return !this.booked[toIso(date)] && !this.lead[toIso(date)];
  };

  /** Keep viewYear/viewMonth inside [minDate, maxDate]. */
  dp.clampView = function () {
    var minY = this.minDate.getFullYear();
    var minM = this.minDate.getMonth();
    var maxY = this.maxDate.getFullYear();
    var maxM = this.maxDate.getMonth();
    if (this.viewYear < minY) {
      this.viewYear = minY;
      this.viewMonth = minM;
    } else if (this.viewYear === minY && this.viewMonth < minM) {
      this.viewMonth = minM;
    }
    if (this.viewYear > maxY) {
      this.viewYear = maxY;
      this.viewMonth = maxM;
    } else if (this.viewYear === maxY && this.viewMonth > maxM) {
      this.viewMonth = maxM;
    }
  };

  /** First month that still has a free day. */
  dp.firstSelectableMonth = function () {
    var sel = this.valueIso;
    if (sel) return isoToDate(sel);
    var d = new Date(this.minDate.getTime());
    for (var i = 0; i < 24; i++) {
      var y = d.getFullYear();
      var m = d.getMonth();
      var last = new Date(y, m + 1, 0).getDate();
      var startDay = i === 0 ? d.getDate() : 1;
      for (var day = startDay; day <= last; day++) {
        if (this.isSelectable(new Date(y, m, day))) return new Date(y, m, 1);
      }
      d.setMonth(m + 1, 1);
    }
    return new Date(this.minDate.getTime());
  };

  dp.open = function () {
    if (!this.input || this.input.disabled || this.calendar) return;
    if (this._suppressOpen) return; // just closed with Escape, do not re-open on focus
    this._insideClick = false;
    var view = this.firstSelectableMonth();
    this.viewYear = view.getFullYear();
    this.viewMonth = view.getMonth();
    this.clampView();
    this.calendar = document.createElement('div');
    this.calendar.className = 'myselfie-datepicker__cal';
    this.calendar.setAttribute('role', 'dialog');
    this.calendar.setAttribute('aria-label', cfg.i18n.pickDate || 'Choose date');
    this.appendChild(this.calendar);
    // The legacy navigation and mobile drawer sit below the sticky announcement
    // bar. Elevate the relevant stacking context only while this popover is open.
    this._stackingParent = this.closest('.Navigation, .Drawer');
    if (this._stackingParent) this._stackingParent.classList.add('myselfie-datepicker-open');
    this.render();
    var self = this;
    setTimeout(function () {
      document.addEventListener('click', self.onDocumentClick);
      document.addEventListener('keydown', self.onKeydown);
    });
  };

  dp.close = function () {
    if (!this.calendar) return;
    if (this.calendar.parentNode) this.calendar.parentNode.removeChild(this.calendar);
    this.calendar = null;
    if (this._stackingParent && !this._stackingParent.querySelector('.myselfie-datepicker__cal')) {
      this._stackingParent.classList.remove('myselfie-datepicker-open');
    }
    this._stackingParent = null;
    this._insideClick = false;
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  };

  /** Capture-phase marker: this click started inside the picker. */
  dp.onInsideClick = function () {
    this._insideClick = true;
  };

  dp.onDocumentClick = function (e) {
    // The click started inside the picker (nav button, select, day). The node
    // may already be detached because render() rewrote the calendar, so
    // contains() cannot be trusted here - the capture-phase flag can.
    if (this._insideClick) {
      this._insideClick = false;
      return;
    }
    var target = e.target;
    if (!target) return;
    // A node that render() removed: never an outside click.
    if (target.nodeType === 1 && !document.documentElement.contains(target)) return;
    // Native <select> popups render outside the element on some platforms.
    var tag = target.tagName;
    if (tag === 'SELECT' || tag === 'OPTION') return;
    if (document.activeElement && document.activeElement.tagName === 'SELECT' && this.contains(document.activeElement)) {
      return;
    }
    if (!this.contains(target)) this.close();
  };

  dp.onKeydown = function (e) {
    if (e.key === 'Escape') {
      var self = this;
      this._suppressOpen = true;
      this.close();
      this.input.focus();
      setTimeout(function () {
        self._suppressOpen = false;
      });
    }
  };

  dp.move = function (delta) {
    var d = new Date(this.viewYear, this.viewMonth + delta, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
    this.clampView();
    this.render();
  };

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  dp.render = function () {
    var i18n = cfg.i18n;
    var dayNames = String(i18n.dayNamesMin || 'Ma,Ti,On,To,Fr,Lø,Sø')
      .split(',')
      .map(function (s) {
        return s.trim();
      });
    var monthNames = String(i18n.monthNames || 'Jan,Feb,Mar,Apr,Maj,Jun,Jul,Aug,Sep,Okt,Nov,Dec')
      .split(',')
      .map(function (s) {
        return s.trim();
      });
    this.clampView();
    var y = this.viewYear;
    var m = this.viewMonth;
    var first = new Date(y, m, 1);
    var daysInMonth = new Date(y, m + 1, 0).getDate();
    var offset = (first.getDay() + 6) % 7; // Monday first
    var selected = this.valueIso;
    var todayIso = toIso(today());
    var minY = this.minDate.getFullYear();
    var minM = this.minDate.getMonth();
    var maxY = this.maxDate.getFullYear();
    var maxM = this.maxDate.getMonth();
    var canPrev = y > minY || m > minM;
    var canNext = y < maxY || m < maxM;

    // Remember which control had focus so a re-render does not drop the user
    // out of the popover (native selects lose focus when innerHTML is replaced).
    var focusKey = this._focusKey || null;
    this._focusKey = null;
    var active = document.activeElement;
    if (!focusKey && active && this.calendar && this.calendar.contains(active)) {
      if (active.classList.contains('myselfie-datepicker__year')) focusKey = '.myselfie-datepicker__year';
      else if (active.classList.contains('myselfie-datepicker__month')) focusKey = '.myselfie-datepicker__month';
      else if (active.hasAttribute('data-nav')) focusKey = '[data-nav="' + active.getAttribute('data-nav') + '"]';
    }

    var years = '';
    for (var yy = minY; yy <= maxY; yy++) {
      years += '<option value="' + yy + '"' + (yy === y ? ' selected' : '') + '>' + yy + '</option>';
    }

    var months = '';
    for (var mm = 0; mm < 12; mm++) {
      var monthDisabled = (y === minY && mm < minM) || (y === maxY && mm > maxM);
      months +=
        '<option value="' +
        mm +
        '"' +
        (mm === m ? ' selected' : '') +
        (monthDisabled ? ' disabled' : '') +
        '>' +
        escapeHtml(monthNames[mm] || String(mm + 1)) +
        '</option>';
    }

    var lowText = i18n.lowCapacity || 'Få tilbage';
    var lowTooltip = i18n.lowTooltip || lowText;
    var popularText = i18n.popular || 'Populær dato';
    var popularTooltip = i18n.popularTooltip || popularText;
    var soldOutText = i18n.fullyBooked || 'Fuldt booket';
    var cells = '';
    var anyPopular = false;
    var anyLow = false;
    var anyBooked = false;
    var leadText = i18n.leadAria || 'kan ikke leveres';
    for (var i = 0; i < offset; i++) cells += '<td></td>';
    for (var day = 1; day <= daysInMonth; day++) {
      var date = new Date(y, m, day);
      var iso = toIso(date);
      // Lead-block days look exactly like days outside the range: disabled, blank, never "fully booked".
      var outsideRange = date < this.minDate || date > this.maxDate;
      var isLead = !outsideRange && this.isLeadDay(iso);
      var isBooked = !!this.booked[iso] && !outsideRange && !isLead;
      var ok = this.ready && this.isSelectable(date);
      var isLow = ok && !!this.low[iso];
      var isPopular = ok && !isLow && !!this.popular[iso];
      if (isPopular) anyPopular = true;
      if (isLow) anyLow = true;
      if (isBooked) anyBooked = true;
      var cls = ['myselfie-datepicker__day'];
      if (!ok) cls.push('myselfie-datepicker__day--disabled');
      if (isBooked) cls.push('myselfie-datepicker__day--booked');
      if (isLow) cls.push('myselfie-datepicker__day--low');
      if (isPopular) cls.push('myselfie-datepicker__day--popular');
      if (ok && !isLow && !isPopular) cls.push('myselfie-datepicker__day--available');
      if (iso === selected) cls.push('myselfie-datepicker__day--selected');
      if (iso === todayIso) cls.push('myselfie-datepicker__day--today');
      var suffix = isLow ? ' – ' + lowTooltip : isPopular ? ' – ' + popularTooltip : isBooked ? ' – ' + soldOutText : isLead ? ' – ' + leadText : '';
      cells +=
        '<td><button type="button" class="' +
        cls.join(' ') +
        '" data-iso="' +
        iso +
        '"' +
        (ok ? '' : ' disabled') +
        ' aria-label="' +
        escapeHtml(toDisplay(date) + suffix) +
        '"' +
        (isLow ? ' title="' + escapeHtml(lowTooltip) + '"' : isPopular ? ' title="' + escapeHtml(popularTooltip) + '"' : '') +
        '>' +
        day +
        '</button></td>';
      if ((offset + day) % 7 === 0 && day !== daysInMonth) cells += '</tr><tr>';
    }

    this.calendar.innerHTML =
      '<div class="myselfie-datepicker__header">' +
      '<button type="button" class="myselfie-datepicker__nav" data-nav="-1" aria-label="' +
      escapeHtml(i18n.prevMonth || 'Forrige måned') +
      '"' +
      (canPrev ? '' : ' disabled') +
      '>&lsaquo;</button>' +
      '<div class="myselfie-datepicker__title">' +
      '<select class="myselfie-datepicker__month" aria-label="' +
      escapeHtml(i18n.month || 'Måned') +
      '">' +
      months +
      '</select>' +
      '<select class="myselfie-datepicker__year" aria-label="' +
      escapeHtml(i18n.year || 'År') +
      '">' +
      years +
      '</select>' +
      '</div>' +
      '<button type="button" class="myselfie-datepicker__nav" data-nav="1" aria-label="' +
      escapeHtml(i18n.nextMonth || 'Næste måned') +
      '"' +
      (canNext ? '' : ' disabled') +
      '>&rsaquo;</button>' +
      '</div>' +
      '<table class="myselfie-datepicker__grid">' +
      '<thead><tr>' +
      dayNames
        .map(function (d) {
          return '<th scope="col">' + escapeHtml(d) + '</th>';
        })
        .join('') +
      '</tr></thead>' +
      '<tbody><tr>' +
      cells +
      '</tr></tbody>' +
      '</table>' +
      (this.ready
        ? ''
        : '<p class="myselfie-datepicker__loading">' + escapeHtml(i18n.loading || 'Indlæser…') + '</p>') +
      '<p class="myselfie-datepicker__legend">' +
      '<span class="myselfie-datepicker__legend-item"><span class="myselfie-datepicker__legend-dot myselfie-datepicker__legend-dot--available"></span>' +
      escapeHtml(i18n.legendAvailable || 'Ledig') +
      '</span>' +
      (anyPopular
        ? '<span class="myselfie-datepicker__legend-item"><span class="myselfie-datepicker__legend-dot myselfie-datepicker__legend-dot--popular"></span>' +
          escapeHtml(i18n.legendPopular || popularText) +
          '</span>'
        : '') +
      (anyLow
        ? '<span class="myselfie-datepicker__legend-item"><span class="myselfie-datepicker__legend-dot myselfie-datepicker__legend-dot--low"></span>' +
          escapeHtml(i18n.legendLow || lowText) +
          '</span>'
        : '') +
      (anyBooked
        ? '<span class="myselfie-datepicker__legend-item"><span class="myselfie-datepicker__legend-dot myselfie-datepicker__legend-dot--booked"></span>' +
          escapeHtml(i18n.legendSoldOut || soldOutText) +
          '</span>'
        : '') +
      '</p>';

    var self = this;
    Array.prototype.forEach.call(this.calendar.querySelectorAll('[data-nav]'), function (b) {
      b.addEventListener('click', function () {
        self.move(Number(b.getAttribute('data-nav')));
      });
    });
    var yearSelect = this.calendar.querySelector('.myselfie-datepicker__year');
    if (yearSelect) {
      yearSelect.addEventListener('change', function (e) {
        self.viewYear = Number(e.target.value);
        self.clampView();
        self._focusKey = '.myselfie-datepicker__year';
        self.render();
      });
    }
    var monthSelect = this.calendar.querySelector('.myselfie-datepicker__month');
    if (monthSelect) {
      monthSelect.addEventListener('change', function (e) {
        self.viewMonth = Number(e.target.value);
        self.clampView();
        self._focusKey = '.myselfie-datepicker__month';
        self.render();
      });
    }
    // Native selects swallow keydown for Escape in some browsers; make sure the
    // popover still closes and Tab keeps working inside the calendar.
    Array.prototype.forEach.call(this.calendar.querySelectorAll('select'), function (s) {
      s.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          self.onKeydown(e);
        }
      });
    });

    if (focusKey) {
      var restore = this.calendar.querySelector(focusKey);
      if (!restore || restore.disabled) {
        restore = this.calendar.querySelector('.myselfie-datepicker__month');
      }
      if (restore) {
        try {
          restore.focus({ preventScroll: true });
        } catch (err) {
          restore.focus();
        }
      }
    }

    Array.prototype.forEach.call(
      this.calendar.querySelectorAll('.myselfie-datepicker__day:not([disabled])'),
      function (b) {
        b.addEventListener('click', function () {
          self.select(b.getAttribute('data-iso'));
        });
      }
    );
  };

  dp.select = function (iso) {
    var date = isoToDate(iso);
    if (!this.isSelectable(date)) return;
    var display = toDisplay(date);
    this.input.value = display;
    if (typeof this.input.setCustomValidity === 'function') this.input.setCustomValidity('');
    storage.save(display);
    this.close();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    document.dispatchEvent(
      new CustomEvent('myselfie:date', {
        detail: { iso: iso, display: display, source: this.dataset.source || 'header' },
      })
    );
  };

  if (!window.customElements.get('myselfie-datepicker')) {
    window.customElements.define('myselfie-datepicker', MyselfieDatepicker);
  }

  /* ------------------------------------------------------------------ */
  /* <myselfie-header-picker>                                            */
  /* ------------------------------------------------------------------ */
  function stripQuery(url) {
    return String(url || '')
      .split('?')[0]
      .split('#')[0]
      .replace(/\/$/, '');
  }

  /** The product page's own jQuery UI picker (assets/api-dates.js), if present. */
  function productPageInput() {
    return document.querySelector(PRODUCT_INPUT_SELECTOR);
  }

  function fillProductPageInput(date, display) {
    var input = productPageInput();
    if (!input) return false;
    var jq = window.jQuery || window.$;
    if (jq && jq.fn && jq.fn.datepicker) {
      try {
        jq(input).datepicker('setDate', date);
      } catch (e) {
        input.value = display;
      }
    } else {
      input.value = display;
    }
    if (!input.value) input.value = display;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    if (jq && typeof jq(input).trigger === 'function') {
      try {
        jq(input).trigger('change');
      } catch (e) {
        /* ignore */
      }
    }
    return true;
  }

  function MyselfieHeaderPicker() {
    return Reflect.construct(HTMLElement, [], MyselfieHeaderPicker);
  }
  MyselfieHeaderPicker.prototype = Object.create(HTMLElement.prototype);
  MyselfieHeaderPicker.prototype.constructor = MyselfieHeaderPicker;
  Object.setPrototypeOf(MyselfieHeaderPicker, HTMLElement);

  var hp = MyselfieHeaderPicker.prototype;

  hp.connectedCallback = function () {
    if (this._bound) return;
    this.picker = this.querySelector('myselfie-datepicker');
    this.status = this.querySelector('[data-status]');
    this.button = this.querySelector('[data-picker-open]');
    if (!this.picker) return;
    this._bound = true;
    var self = this;
    if (this.button) {
      this.button.addEventListener('click', function () {
        if (self.picker.open) self.picker.open();
      });
    }
    this.addEventListener('change', function (e) {
      if (self.picker && e.target === self.picker.input) self.onPick();
    });
    this.onExternalDate = this.onExternalDate.bind(this);
    document.addEventListener('myselfie:date', this.onExternalDate);
    // Reverse sync: the product page's jQuery UI picker (assets/api-dates.js) and
    // anything else calling its saveDate() fire "datepicker:saved" with
    // {selectedDate:"DD/MM/YYYY"}; mirror that date into the header field.
    this.onLegacyDate = this.onLegacyDate.bind(this);
    document.addEventListener('datepicker:saved', this.onLegacyDate);
    availability.load().then(function () {
      self.restore();
    });
  };

  hp.disconnectedCallback = function () {
    document.removeEventListener('myselfie:date', this.onExternalDate);
    document.removeEventListener('datepicker:saved', this.onLegacyDate);
  };

  /** A date saved by the legacy picker (DD/MM/YYYY) lands in the header field too. */
  hp.onLegacyDate = function (e) {
    var display = (e && e.detail && e.detail.selectedDate) || '';
    var iso = displayToIso(display);
    if (!iso || !this.picker || !this.picker.input) return;
    if (this.picker.input.value !== display) this.picker.input.value = display;
    this.showStatus(iso);
  };

  /** Keep the desktop and drawer instances in sync. */
  hp.onExternalDate = function (e) {
    var detail = (e && e.detail) || {};
    if (detail.cleared && this.picker && this.picker.input) {
      // the cart was emptied, so the booking date is forgotten (assets/myselfie-booking-calendar.js)
      this.picker.input.value = '';
      if (this.status) {
        this.status.textContent = '';
        this.status.removeAttribute('data-state');
        this.status.hidden = true;
      }
      return;
    }
    if (!detail.iso || !this.picker || !this.picker.input) return;
    if (this.picker.input.value !== detail.display) this.picker.input.value = detail.display || '';
    this.showStatus(detail.iso);
  };

  /** On load: prefill the status line from the stored date. */
  hp.restore = function () {
    if (!this.picker || !this.picker.input) return;
    var stored = storage.read();
    if (stored && !this.picker.input.value) this.picker.input.value = stored;
    var iso = this.picker.valueIso;
    if (!iso) return;
    this.showStatus(iso);
  };

  /** A remembered date that fell into the lead block is not accepted: field, storage and status are emptied. */
  hp.dropLeadDate = function (iso) {
    if (!this.picker || !this.picker.input) return false;
    if (!availability.isLead(iso, this.picker.components)) return false;
    var display = toDisplay(isoToDate(iso));
    this.picker.input.value = '';
    if (storage.read() === display) storage.clear();
    if (this.status) {
      this.status.textContent = '';
      this.status.removeAttribute('data-state');
      this.status.hidden = true;
    }
    return true;
  };

  hp.showStatus = function (iso) {
    if (!this.status) return;
    if (this.dropLeadDate(iso)) return;
    var state = availability.statusFor(iso, this.picker.components);
    var text;
    if (state === 'sold_out') text = cfg.i18n.statusSoldOut || cfg.i18n.fullyBooked || 'Fuldt booket';
    else if (state === 'low') text = cfg.i18n.statusLow || cfg.i18n.lowCapacity || 'Få tilbage';
    else if (state === 'popular') text = cfg.i18n.statusPopular || cfg.i18n.popular || 'Populær dato';
    else text = cfg.i18n.statusAvailable || 'Ledig';
    this.status.textContent = text;
    this.status.setAttribute('data-state', state);
    this.status.hidden = false;
  };

  hp.onPick = function () {
    var iso = this.picker.valueIso;
    if (!iso) return;
    var display = this.picker.input.value;
    storage.save(display);
    this.showStatus(iso);

    // On a product page: drive the existing jQuery UI picker instead of navigating.
    if (fillProductPageInput(isoToDate(iso), display)) return;

    var url = this.getAttribute('data-target-url') || '';
    if (!url) return;
    if (stripQuery(url) === stripQuery(location.pathname)) {
      var hash = url.split('#')[1];
      var anchor = hash ? document.getElementById(hash) : null;
      if (anchor && typeof anchor.scrollIntoView === 'function') {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (hash) {
        location.hash = hash;
      }
      return;
    }
    location.href = url;
  };

  if (!window.customElements.get('myselfie-header-picker')) {
    window.customElements.define('myselfie-header-picker', MyselfieHeaderPicker);
  }

  /* Exposed for debugging / other scripts. */
  cfg.headerPicker = {
    storageKey: STORAGE_KEY,
    storage: storage,
    toIso: toIso,
    toDisplay: toDisplay,
    displayToIso: displayToIso,
    isoToDate: isoToDate,
  };
})();
